import { useAppointmentsStore } from '../stores/useAppointmentsStore';
import { useInvoicesStore } from '../stores/useInvoicesStore';
import { useQuotesStore } from '../stores/useQuotesStore';

/**
 * Centralized fan-out that resets every user-scoped Pinia store. Invoked by
 * useAuthStore.logout() and useAuthStore.handleUnauthorized() so a subsequent
 * login as a different account cannot briefly render the prior account's
 * appointment / invoice / quote data before a refetch completes.
 *
 * Purpose: closes a cross-account in-memory data-exposure window discovered
 * during QA Plan A for PODZ-1544. logout() clears auth state but leaves the
 * report stores holding the previous account's data.
 *
 * Covered (per-account) stores:
 *   - useAppointmentsStore (appointment / job / comments / availability / report
 *     surface / upload registries)
 *   - useInvoicesStore (report surface)
 *   - useQuotesStore (report surface)
 *
 * Intentionally NOT reset (not per-account):
 *   - useTenantStore — per-subdomain branding
 *   - useThemeStore — device preferences persisted to localStorage
 *   - useAuthStore — handled by logout() / handleUnauthorized() themselves
 *
 * Mirrors the useReportsFanOut composable shape.
 */
export interface ResetUserScopedStores {
  resetAll: () => void;
}

export function useResetUserScopedStores(): ResetUserScopedStores {
  const appointmentsStore = useAppointmentsStore();
  const invoicesStore = useInvoicesStore();
  const quotesStore = useQuotesStore();

  function resetAll(): void {
    appointmentsStore.reset();
    invoicesStore.reset();
    quotesStore.reset();
  }

  return {
    resetAll,
  };
}
